import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { Trophy, Crown, Medal, Star, MapPin } from "lucide-react";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

const RANK_META = {
  1: { I: Crown, color: "from-amber-400 to-amber-600", label: "🥇" },
  2: { I: Medal, color: "from-neutral-300 to-neutral-500", label: "🥈" },
  3: { I: Medal, color: "from-orange-400 to-orange-600", label: "🥉" },
};

export default function Leaderboard() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [testimonials, setTestimonials] = useState({ avg: null, count: 0, items: [] });

  useEffect(() => {
    api.get("/leaderboard?limit=20").then((r) => setItems(r.data)).catch(() => {});
    api.get("/public/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <PublicLayout>
      <Seo title={t("comp_leaderboard_90")} description="Le classement des membres les plus engagés de la communauté." path="/community/top" />
      <section className="mx-auto max-w-5xl px-6 py-12">
        <div className="text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-50"><Trophy className="h-6 w-6 text-amber-600" /></div>
          <h1 className="mt-4 text-4xl font-bold text-navy md:text-5xl">{t("comp_leaderboard_90")}</h1>
          <p className="mt-3 text-neutral-600">{t("comp_leaderboard_91")}</p>
        </div>

        {testimonials.avg !== null && testimonials.count > 0 && (
          <div className="mt-8 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-wine-50 p-6 text-center shadow-sm" data-testid="testimonials-banner">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700">Satisfaction membres</div>
            <div className="mt-1 font-serif text-6xl font-bold text-amber-600">{testimonials.avg}<span className="text-2xl text-neutral-500">/5</span></div>
            <div className="mt-2 inline-flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-5 w-5 ${testimonials.avg >= n - 0.5 ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />)}
            </div>
            <div className="mt-1 text-xs text-neutral-500">Basé sur {testimonials.count} avis</div>
          </div>
        )}

        <div className="mt-8 space-y-2">
          {items.map((c, idx) => {
            const rank = idx + 1;
            const meta = RANK_META[rank];
            return (
              <Link key={c.id} to={`/members/${c.slug}`} className="flex items-center gap-4 rounded-2xl border border-neutral-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={`leaderboard-row-${c.id}`}>
                <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-full font-serif text-xl font-bold ${meta ? `bg-gradient-to-br ${meta.color} text-white shadow-md` : "bg-neutral-100 text-neutral-600"}`}>
                  {meta ? meta.label : rank}
                </div>
                {c.logo && <img src={resolveUrl(c.logo)} alt={c.name} className="h-10 w-10 rounded-lg object-contain bg-white p-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="truncate font-serif text-base font-bold text-neutral-900">{c.name}</div>
                  <div className="text-xs text-neutral-500"><MapPin className="inline h-3 w-3" /> {c.city}, {c.country}</div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-2xl font-bold text-wine-700">{c.score}</div>
                  <div className="text-[10px] uppercase text-neutral-500">points</div>
                </div>
              </Link>
            );
          })}
          {items.length === 0 && <div className="rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/50 px-6 py-16 text-center text-sm text-neutral-500">{t("comp_leaderboard_92")}</div>}
        </div>

        {testimonials.items?.length > 0 && (
          <>
            <h2 className="mt-12 font-serif text-2xl text-neutral-900">{t("comp_leaderboard_93")}</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {testimonials.items.map((t, i) => (
                <div key={i} className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid={`testimonial-${i}`}>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${t.rating >= n ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />)}
                  </div>
                  <p className="mt-2 text-sm italic text-neutral-700">« {t.comment} »</p>
                  <div className="mt-3 flex items-center gap-2">
                    {t.company.logo && <img src={resolveUrl(t.company.logo)} alt={t.company.name} className="h-7 w-7 rounded-md object-contain bg-white p-0.5" />}
                    <Link to={`/members/${t.company.slug}`} className="text-xs font-bold text-wine-700 hover:underline">{t.company.name}</Link>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </PublicLayout>
  );
}
